import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  CardHeader,
  Checkbox,
  FormControlLabel,
  TextField,
  Box,
} from "@mui/material";
import { Stack } from "@mui/system";
import SelectionLimitDialog from "./SelectionLimitDialog";
import {
  createCategory,
  deleteCategory,
  getCategories,
} from "../../store/actions/category";
import { WithHeader } from "./WithHeader";
import { getFilterCategories } from "../../store/reducers/selector";
import { setCategories } from "../../store/actions/currentArticle";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import ConfirmationDialog from "../dialogs/ConfirmationDialog";
import { toast } from "react-toastify";

const DIALOG_TITLE = "Confirm";
const DIALOG_CONTENT = "Are you sure?";

const CategorySelector = () => {
  const dispatch = useDispatch();
  const [titleValue, setTitleValue] = useState("");
  const [error, setError] = useState(false);
  const [categoryError, setCategoryError] = useState(false);
  const [selected, setSelected] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const filteredCategories = useSelector(getFilterCategories);
  const languageSelector = useSelector((state) => state.article.language);
  const selectedCategories = useSelector((state) => state.article.categoryIds);
  let selectedCategoriesId = selectedCategories.flatMap((c) => c.id);

  const handleAddClick = () => {
    const existingCategory = filteredCategories.find(
      (c) =>
        c.title[languageSelector].toLowerCase() === titleValue.toLowerCase()
    );

    if (!titleValue.trim()) {
      setCategoryError("Please enter a valid category name.");
      return;
    }
    setCategoryError("");

    if (existingCategory) {
      // Category already exists, do something
      setError(true);
      return;
    } else {
      // Category doesn't exist, create a new one
      dispatch(createCategory(titleValue.trim(), languageSelector))
        .then(() => {
          dispatch(getCategories());
        })
        .then(() => {
          setTitleValue("");
        });
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };
  const handleToggleCategory = (id, categoryTitle) => () => {
    const currentIndex = selected.indexOf(categoryTitle);
    const selectedCount = selectedCategories.length;
    const isCategorySelected = selectedCategoriesId.includes(id);

    if (selectedCount === 5 && !isCategorySelected) {
      setDialogOpen(true);
      return;
    }

    const newSelected = [...selected];
    if (currentIndex === -1) {
      newSelected.push(categoryTitle);
    } else {
      newSelected.splice(currentIndex, 1);
    }
    setSelected(newSelected);
    dispatch(setCategories(id));
  };

  const handleDeleteClick = (id) => {
    setShowConfirmationDialog(true);
    setSelectedId(id);
  };

  const handleDeleteCategory = () => {
    dispatch(deleteCategory(selectedId))
      .then((res) => {
        toast.success("Category deleted Successfully");
        dispatch(getCategories());
      })
      .catch((e) => {
        toast.error(
          "This is category cannot be deleted. It is linked with other learning resource"
        );
        // setShowConfirmationDialog(true);
      });
  };

  const getCategoryRow = (item) => {
    const categoryTitle = item.title[languageSelector];
    return (
      <Stack
        key={item.id}
        alignItems="center"
        direction="row"
        justifyContent="flex-start"
      >
        <FormControlLabel
          value={item.id}
          sx={{
            m: 0,
          }}
          control={
            <Checkbox
              checked={selectedCategoriesId.includes(item.id)}
              onChange={handleToggleCategory(item.id, item.title)}
            />
          }
        />
        <CardHeader
          key={item.id}
          title={categoryTitle}
          sx={{
            p: 0,
          }}
          titleTypographyProps={{
            fontSize: "14px", // Adjusts the font size of the MuiTypography-root
          }}
        />

        {filteredCategories?.length !== 1 && (
          <IconButton
            onClick={() => handleDeleteClick(item.id)}
            // disabled={filteredCategories?.length === 1}
            aria-label="delete"
            size="small"
            sx={{
              ml: "auto",
              mr: 2,
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 20 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3.75 23.25C3.0625 23.25 2.47417 23.0054 1.985 22.5163C1.49583 22.0271 1.25083 21.4383 1.25 20.75V4.5H0V2H6.25V0.75H13.75V2H20V4.5H18.75V20.75C18.75 21.4375 18.5054 22.0263 18.0163 22.5163C17.5271 23.0063 16.9383 23.2508 16.25 23.25H3.75ZM16.25 4.5H3.75V20.75H16.25V4.5ZM6.25 18.25H8.75V7H6.25V18.25ZM11.25 18.25H13.75V7H11.25V18.25Z"
                fill="black"
                fill-opacity="0.5"
              />
            </svg>
          </IconButton>
        )}
      </Stack>
    );
  };

  return (
    <>
      <ConfirmationDialog
        open={showConfirmationDialog}
        title={DIALOG_TITLE}
        content={DIALOG_CONTENT}
        onClose={() => {
          setShowConfirmationDialog(false);
        }}
        onCancel={() => {
          setShowConfirmationDialog(false);
        }}
        onDelete={handleDeleteCategory}
        showCancel={true}
        showDelete={true}
      />
      <Box className="cs-scroll" sx={{ maxHeight: 400, overflowY: "auto" }}>
        {filteredCategories.length > 0 &&
          filteredCategories.map((item) => getCategoryRow(item))}
      </Box>
      <TextField
        fullWidth
        type="text"
        value={titleValue}
        onChange={(e) => {
          setTitleValue(e.target.value);
          setError(false);
          setCategoryError("");
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: 0, // set borderRadius to 0
          },
        }}
        placeholder="Start typing to create a new category"
        InputProps={{
          endAdornment: titleValue && (
            <Button
              size="sm"
              variant="contained"
              onClick={handleAddClick}
              sx={{ width: "200px" }}
            >
              Add Category
            </Button>
          ),
        }}
        error={error}
        helperText={error && "Category already exists!"}
      />
      {categoryError && (
        <span style={{ color: "red", marginTop: "5px" }}>{categoryError}</span>
      )}
      <SelectionLimitDialog
        data="Categories"
        count="5"
        open={dialogOpen}
        onClose={handleCloseDialog}
      />
    </>
  );
};
export default WithHeader(CategorySelector, "Category *");
